import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    Injector,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';

import { LinkDirective } from '../../../../_shared/directives/link.directive';
import { ICardFlip } from '../../../../_shared/models/card/card-flip.model';
import { ImageData } from '../../../../_shared/models/image/image-data.model';
import { EMPTY_LINK, ILink } from '../../../../_shared/models/link.model';
import { MediaImage } from '../../../../_shared/models/media/media-image.model';
import { withConfigFallback } from '../../../../_shared/utils/with-config-fallback';
import { IconComponent } from '../../../../icon/icon.component';
import { ImageItemComponent } from '../../../../media/media-item/image-item/image-item.component';
import { TextHtmlComponent } from '../../../../text-html/text-html.component';

let nextCardFlipId = 0;

@Component({
    selector: 'sirius-card-flip',
    templateUrl: './card-flip.component.html',
    styleUrls: ['./card-flip.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IconComponent, ImageItemComponent, LinkDirective, TextHtmlComponent],
    host: {
        class: 'sir-card-flip',
        '[class.is-flipped]': 'isFlipped()',
        '(click)': 'toggleFlip($event)',
        '(keydown)': 'onKeydown($event)',
    },
})
export class CardFlipComponent {
    private readonly _injector = inject(Injector);
    private readonly _instanceId = nextCardFlipId++;

    readonly backCtaLink = viewChild<ElementRef<HTMLAnchorElement>>('backCtaLink');
    readonly flipToggle = viewChild<ElementRef<HTMLButtonElement>>('flipToggle');
    readonly config = input<ICardFlip | undefined>(undefined);
    readonly imageData = input<ImageData | undefined>(undefined);

    readonly image = input<MediaImage | undefined>(undefined);
    readonly captionHtml = input('');
    readonly backHtml = input('');
    readonly link = input<ILink>(EMPTY_LINK);

    readonly selectEvent = output<void>();

    protected readonly resolvedImage = withConfigFallback<ICardFlip, 'image'>(this.config, 'image', this.image);
    protected readonly resolvedCaptionHtml = withConfigFallback<ICardFlip, 'captionHtml', string>(
        this.config,
        'captionHtml',
        this.captionHtml
    );
    protected readonly resolvedBackHtml = withConfigFallback<ICardFlip, 'backHtml', string>(
        this.config,
        'backHtml',
        this.backHtml
    );
    protected readonly resolvedLink = withConfigFallback<ICardFlip, 'link', ILink>(this.config, 'link', this.link);

    protected readonly isFlipped = signal(false);

    protected readonly flipToggleId = `sir-card-flip-toggle-${this._instanceId}`;
    protected readonly backFaceId = `sir-card-flip-back-${this._instanceId}`;

    protected readonly hasImage = computed(() => !!this.resolvedImage());

    /** Empty alt when the image title repeats the visible caption (avoids duplicate announcements). */
    protected readonly imageAlt = computed(() => {
        const caption = this.resolvedCaptionHtml().trim();
        if (!caption) {
            return undefined;
        }

        const title = this.resolvedImage()?.title?.trim();
        if (!title || title === caption) {
            return '';
        }

        return undefined;
    });

    protected readonly showCta = computed(() => {
        const link = this.resolvedLink();
        return !!link.href?.trim() && !!link.title?.trim();
    });

    protected readonly flipToggleLabel = computed(() => {
        const caption = this.resolvedCaptionHtml().trim();
        const subject = caption || 'card';

        return this.isFlipped() ? `Show front of ${subject}` : `Show details for ${subject}`;
    });

    /** Region name for the back face — the card subject, not the flip control action. */
    protected readonly backFaceLabel = computed(() => this.resolvedCaptionHtml().trim() || 'Card details');

    toggleFlip(event?: Event, manageFocus = false): void {
        if (event) {
            const target = event.target as HTMLElement | null;
            if (target?.closest('a')) {
                return;
            }

            event.preventDefault();
        }

        const flippingToBack = !this.isFlipped();

        this.isFlipped.update((flipped) => !flipped);

        if (!manageFocus) {
            return;
        }

        afterNextRender(
            () => {
                if (flippingToBack && this.showCta()) {
                    this.backCtaLink()?.nativeElement.focus({ preventScroll: true });
                    return;
                }

                if (!flippingToBack) {
                    this.flipToggle()?.nativeElement.focus({ preventScroll: true });
                }
            },
            { injector: this._injector }
        );
    }

    onLinkClick(): void {
        this.selectEvent.emit();
    }

    protected onKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Escape' || !this.isFlipped()) {
            return;
        }

        event.preventDefault();
        this.isFlipped.set(false);

        afterNextRender(
            () => this.flipToggle()?.nativeElement.focus({ preventScroll: true }),
            { injector: this._injector }
        );
    }
}
